import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useCodingOpenStore } from '~/lib/stores/coding-open-store';
import { shouldCloseForNewOperatorPanelRequest } from '~/lib/stores/operator-panel-close-gate';
import { useMarkProBuilderSeen } from '~/hooks/mutations/use-pro-builder';
import { useFeatureFlags } from '~/hooks/queries/use-feature-flags';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useCloseOnOperatorPanelRequest } from '~/lib/stores/use-close-on-operator-panel-request';
import { WindowShell, WindowSystemContext } from '~/features/windows/window-system';
import { PRO_BUILDER_WINDOW_ID } from '~/features/windows/window-titles';
import { ProBuilderBody } from './pro-builder-body';
import { ProBuilderWindow } from './pro-builder-window';
import {
  proBuilderFlagFromData,
  shouldHideOnNavigation,
  shouldMarkSeen,
  shouldMountProBuilderWindow,
} from './pro-builder-window-rules';

/**
 * FRONT-3223, standalone only (`eddy.ui-refactoring` off): hides the window the
 * instant a NEW one-shot open request lands in a store (modal viewers are
 * mutually exclusive, never stacked). A request already set when the host
 * mounts is not "new" (same seeding rule as `useCloseOnOperatorPanelRequest`).
 */
function useCloseOnNewRequest(request: unknown, close: () => void, active: boolean): void {
  const previous = useRef(request);
  useEffect(() => {
    if (active && shouldCloseForNewOperatorPanelRequest(previous.current, request)) close();
    previous.current = request;
  }, [request, close, active]);
}

/** Standalone only: hides the window on a location change while it is open (see `shouldHideOnNavigation`). */
function useCloseOnNavigation(close: () => void, active: boolean): void {
  const { pathname } = useLocation();
  const previous = useRef(pathname);
  useEffect(() => {
    const open = useProBuilderOpenStore.getState().open;
    if (active && shouldHideOnNavigation(previous.current, pathname, open)) close();
    previous.current = pathname;
  }, [pathname, close, active]);
}

/**
 * FRONT-3040: marks the session's state seen on each visibility edge (see
 * `shouldMarkSeen`), so the board row turns grey once the person has looked.
 * The mutation is only ever called, never reset (it may be pending).
 */
function useMarkSeenOnVisibilityEdge(open: boolean): void {
  const { mutate } = useMarkProBuilderSeen();
  const previous = useRef(open);
  useEffect(() => {
    if (shouldMarkSeen(previous.current, open)) mutate();
    previous.current = open;
  }, [open, mutate]);
}

/** Mounted once in the app shell (`layouts/app-layout.tsx`) so page navigation never unmounts the terminal. */
export function ProBuilderWindowHost() {
  // Deliberately NOT `useFeatureFlag`: that falls back to false on a failed
  // refetch, which is right for showing a doorway (the sidebar, and Settings →
  // Pro Builder with its Restart button, which reads the flag that way on
  // purpose) and wrong here, where an unmount detaches the person from a running Claude Code.
  // This decides from the last flags DATA and unmounts only when it positively
  // says the flag is off. Two different rules for one flag, by design.
  const { data } = useFeatureFlags();
  const everOpened = useProBuilderOpenStore((s) => s.everOpened);
  const open = useProBuilderOpenStore((s) => s.open);
  const close = useProBuilderOpenStore((s) => s.closeProBuilder);
  const wasMounted = useRef(false);
  // Docked in the window layer whenever the app shell provides one (it always does); standalone otherwise.
  const docked = useContext(WindowSystemContext);
  // FRONT-3146: chat and Build windows no longer hide this one, and neither
  // does navigating — windows coexist in the window layer and persist across
  // pages. The desktop operator panel is still a full-screen overlay, so it
  // keeps the old "never stacked" rule in both directions
  // (`use-close-on-pro-builder-open.ts` covers the other one).
  useCloseOnOperatorPanelRequest(close);
  // Standalone (FRONT-3223, windows flag off): the pre-FRONT-3146 "never stacked" rules.
  useCloseOnNewRequest(useCodingOpenStore((s) => s.openRequest), close, !docked);
  useCloseOnNewRequest(useChatOpenStore((s) => s.openRequest), close, !docked);
  useCloseOnNewRequest(useChatOpenStore((s) => s.openDocumentRequest), close, !docked);
  useCloseOnNavigation(close, !docked);
  useMarkSeenOnVisibilityEdge(open);

  const mounted = shouldMountProBuilderWindow(proBuilderFlagFromData(data), everOpened, wasMounted.current);
  wasMounted.current = mounted;
  if (!mounted) return null;
  // The slot outlives the window (see `WindowLayer`), so closing only hides it
  // and the terminal inside keeps running.
  return (
    <WindowShell
      id={PRO_BUILDER_WINDOW_ID}
      kind="pro-builder"
      refId={null}
      title="Pro Builder"
      hosted
      keepMounted
      onClose={close}
    >
      <ProBuilderWindow open={open} onClose={close} docked={docked}>
        <ProBuilderBody onClose={close} />
      </ProBuilderWindow>
    </WindowShell>
  );
}
